import { ReactNode, useCallback, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import SpellIcon from './SpellIcon'
import EncounterAbilityLabel from './EncounterAbilityLabel'
import TeamPlanActorRows from './TeamPlanActorRows'
import { useTeamPlanTimeline } from './TeamPlanTimelineContext'
import TimelineContainer from './TimelineContainer'
import TimelineAbilitySection from './TimelineAbilitySection'
import { Separator } from '@/components/ui/separator'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import { NOTE_ABILITY_ID, NOTE_ICON_NAME } from '../config/generalAbilities'
import { abilityTypes, AbilityType } from 'app/frontend/types/custom/Abilities'
import AddActorBar from './AddActorBar'
import ActorTimelineContainer, {
  ActorTimelineRow,
  sortActorTimelineRows,
} from './ActorTimelineContainer'
import ActorAbilityContainer from './ActorAbilityContainer'
import ActorHeader from './ActorHeader'
import TeamPlanAssignments from './TeamPlanAssignments'
import mapGeneralCastsWithOverlap from './generalCastOverlap'
import { getViewerColorClass, getViewerLabel } from './viewerPresence'
import { getRaidPlanAbilities } from 'app/frontend/base/services/raidPlanData'
import { getEncounterAssignmentAbilities } from './assignmentTimeline'
import { useStickyTop } from 'app/frontend/base/hooks/useStickyTop'
import { Button } from '@/components/ui/button'
import Card from 'app/frontend/base/components/Card'
import {
  applyPhaseBoundaryChange,
  buildPhaseDragBaseline,
  type PhaseDragBaseline,
} from '../utils/phaseDurationEdit'
import ImportFromWarcraftLogsDialog from './ImportFromWarcraftLogsDialog'
import CopyFromPlanDialog from './CopyFromPlanDialog'
import { imageUrl } from 'app/frontend/base/helpers'
import ImportFromNoteDialog from './ImportFromNoteDialog'
import PlanImportStringDialog from './PlanImportStringDialog'
import {
  countRaidPlanContent,
  parseRaidPlanImport,
  resolveImportedRaidPlanTimeline,
  type RaidPlanImportSuccess,
} from './raidPlanTransfer'
import { CopyIcon, FileTextIcon, Loader2, Pin } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value))

const ENCOUNTER_TIMELINE_PIN_STORAGE_KEY = 'raid-plan-encounter-timeline-pinned'

const readEncounterTimelinePinned = () => {
  if (typeof window === 'undefined') return true
  const stored = localStorage.getItem(ENCOUNTER_TIMELINE_PIN_STORAGE_KEY)
  if (stored === null) return true
  return stored === 'true'
}

type TeamPlanTimelineProps = {
  inFocusedDialog?: boolean
  actorRowBehaviorByUuid?: Record<
    string,
    {
      showPreferredCharacter?: boolean
      allowRemove?: boolean
      allowSpecializationEdit?: boolean
      allowCastEdit?: boolean
    }
  >
  beforeActorContentByUuid?: Record<string, ReactNode>
  extraActorContent?: ReactNode
}

const TeamPlanTimeline = ({
  inFocusedDialog = false,
  actorRowBehaviorByUuid,
  beforeActorContentByUuid,
  extraActorContent,
}: TeamPlanTimelineProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const {
    canEdit,
    owner,
    planListPathPrefix,
    rosterMemberships,
    raidPlan,
    viewers,
    timeline,
    isInitializing,
    computed,
    selectedType,
    setSelectedType,
    zoom,
    timelineRootRef,
    cursors,
    cursorContext,
    sendCursor,
    updateTimeline,
    setDragFrameMax,
    applyPlanFromSource,
  } = useTeamPlanTimeline()
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false)
  const [isImportFromNoteDialogOpen, setIsImportFromNoteDialogOpen] =
    useState(false)
  const [isImportStringDialogOpen, setIsImportStringDialogOpen] =
    useState(false)
  const [isCopyFromPlanDialogOpen, setIsCopyFromPlanDialogOpen] =
    useState(false)
  const [isEncounterTimelinePinned, setIsEncounterTimelinePinned] = useState(
    readEncounterTimelinePinned,
  )
  const toggleEncounterTimelinePinned = useCallback(() => {
    setIsEncounterTimelinePinned((current) => {
      const next = !current
      localStorage.setItem(ENCOUNTER_TIMELINE_PIN_STORAGE_KEY, String(next))
      return next
    })
  }, [])
  const {
    encounter,
    hasEncounterTimeline,
    maxTime,
    phaseDividers,
    phaseBoundaryHandles,
    encounterAbilityRows,
    generalAbilities,
    phaseOrder,
    effectivePhaseDurations,
    phaseStarts,
    phaseEnds,
    resolvePhaseIndexAtTime,
  } = computed
  const visibleRange = useMemo(() => {
    const start = clamp(zoom?.start ?? 0, 0, maxTime)
    const end = clamp(zoom?.end ?? maxTime, start + 1, maxTime)
    return { start, end }
  }, [maxTime, zoom?.end, zoom?.start])
  const [hoverTime, setHoverTime] = useState<number | null>(null)
  const visibleHoverTime = hoverTime
  const generalRows = useMemo<ActorTimelineRow[]>(
    () =>
      generalAbilities.map((ability) => {
        const rowSpellId = ability.id === NOTE_ABILITY_ID ? null : ability.id
        return {
          key: `general-${ability.id}`,
          spellId: rowSpellId,
          casts: mapGeneralCastsWithOverlap(
            (timeline.general?.casts ?? []).filter(
              (cast) => cast.spell_id === rowSpellId && !cast.hidden,
            ),
            ability.cooldown ?? 0,
            'var(--color-primary)',
          ),
        }
      }),
    [generalAbilities, timeline.general?.casts],
  )
  const generalAbilityById = useMemo(
    () =>
      generalAbilities.reduce<Record<number, typeof generalAbilities[number]>>(
        (acc, ability) => {
          acc[ability.id] = ability
          return acc
        },
        {},
      ),
    [generalAbilities],
  )
  const sortedGeneralRows = useMemo(
    () =>
      sortActorTimelineRows(
        generalRows,
        (spellId) => {
          if (spellId === null) {
            return (
              generalAbilities.find((ability) => ability.id === NOTE_ABILITY_ID)
                ?.name ?? 'Note'
            )
          }
          return generalAbilityById[spellId]?.name ?? `Spell ${spellId}`
        },
        (spellId) =>
          spellId === null ? 0 : generalAbilityById[spellId]?.cooldown ?? 0,
      ),
    [generalAbilities, generalAbilityById, generalRows],
  )

  const lastCursorAtRef = useRef(0)
  const handleTimelineMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const now = Date.now()
      if (now - lastCursorAtRef.current < 100) return
      lastCursorAtRef.current = now
      const rect = event.currentTarget.getBoundingClientRect()
      if (rect.width <= 0 || rect.height <= 0) return
      const x = (event.clientX - rect.left) / rect.width
      const y = (event.clientY - rect.top) / rect.height
      sendCursor(
        Number(clamp(x, 0, 1).toFixed(4)),
        Number(clamp(y, 0, 1).toFixed(4)),
        cursorContext,
      )
    },
    [cursorContext, sendCursor],
  )
  const handleTimelineMouseLeave = useCallback(() => {
    sendCursor(null, null, cursorContext)
    if (boundaryDragActiveRef.current) return
    setHoverTime(null)
  }, [cursorContext, sendCursor])

  const phaseDragBaselineRef = useRef<PhaseDragBaseline | null>(null)
  const boundaryDragActiveRef = useRef(false)

  const capturePhaseDragBaseline = useCallback(() => {
    phaseDragBaselineRef.current = buildPhaseDragBaseline({
      timeline,
      phaseOrder,
      phaseStarts,
      phaseEnds,
      durations: effectivePhaseDurations,
      handles: phaseBoundaryHandles,
    })
  }, [
    effectivePhaseDurations,
    phaseBoundaryHandles,
    phaseEnds,
    phaseOrder,
    phaseStarts,
    timeline,
  ])

  const applyPhaseBoundary = useCallback(
    (boundaryIndex: number, nextAt: number, pushHistory: boolean) => {
      if (!phaseDragBaselineRef.current) capturePhaseDragBaseline()
      const baseline = phaseDragBaselineRef.current
      if (!baseline) return
      const transformed = applyPhaseBoundaryChange(
        baseline,
        boundaryIndex,
        nextAt,
      )
      if (!transformed) return

      updateTimeline((next) => {
        Object.keys(next).forEach((key) => {
          delete (next as Record<string, unknown>)[key]
        })
        Object.assign(next, transformed)
      }, pushHistory)
    },
    [capturePhaseDragBaseline, updateTimeline],
  )

  const handlePhaseBoundaryDragStart = useCallback(
    (_boundaryIndex: number, at: number) => {
      // Freeze the frame at the current length only. Shortening then reveals the
      // black area to the right (frame acts as a floor) while lengthening lets
      // the timeline grow past it. Because we don't expand the frame up front,
      // the grabbed boundary stays exactly under the cursor with no snap.
      const baselineSum = effectivePhaseDurations.reduce(
        (sum, duration) => sum + duration,
        0,
      )
      setDragFrameMax(baselineSum)
      capturePhaseDragBaseline()
      boundaryDragActiveRef.current = true
      setHoverTime(at)
    },
    [capturePhaseDragBaseline, effectivePhaseDurations, setDragFrameMax],
  )

  const handlePhaseBoundaryDrag = useCallback(
    (boundaryIndex: number, nextAt: number) => {
      applyPhaseBoundary(boundaryIndex, nextAt, false)
      setHoverTime(nextAt)
    },
    [applyPhaseBoundary],
  )

  const handlePhaseBoundaryDragEnd = useCallback(
    (boundaryIndex: number, nextAt: number) => {
      applyPhaseBoundary(boundaryIndex, nextAt, true)
      phaseDragBaselineRef.current = null
      setDragFrameMax(null)
      boundaryDragActiveRef.current = false
      setHoverTime(nextAt)
    },
    [applyPhaseBoundary, setDragFrameMax],
  )

  const handleResetPhaseDuration = useCallback(
    (phaseIndex: number) => {
      const handle = phaseBoundaryHandles.find(
        (entry) => entry.index === phaseIndex,
      )
      if (!handle) return
      capturePhaseDragBaseline()
      applyPhaseBoundary(phaseIndex, handle.max, true)
      phaseDragBaselineRef.current = null
    },
    [applyPhaseBoundary, capturePhaseDragBaseline, phaseBoundaryHandles],
  )

  const swapPhases = useCallback(
    (displayIndex: number, direction: 'left' | 'right') => {
      if (!canEdit) return
      const divider = phaseDividers[displayIndex]
      if (!divider) return
      if (direction === 'left' && !divider.canMoveLeft) return
      if (direction === 'right' && !divider.canMoveRight) return
      const swapWithIndex =
        direction === 'left' ? displayIndex - 1 : displayIndex + 1
      if (swapWithIndex < 0 || swapWithIndex >= phaseOrder.length) return

      const newOrder = [...phaseOrder]
      const temp = newOrder[displayIndex]
      newOrder[displayIndex] = newOrder[swapWithIndex]
      newOrder[swapWithIndex] = temp

      let running = 0
      const newStarts = new Array(effectivePhaseDurations.length).fill(0)
      for (const phaseIndex of newOrder) {
        newStarts[phaseIndex] = running
        running += effectivePhaseDurations[phaseIndex] ?? 0
      }
      const deltas = phaseStarts.map(
        (oldStart, phaseIndex) => (newStarts[phaseIndex] ?? 0) - oldStart,
      )

      updateTimeline((next) => {
        next.actors.forEach((actor) => {
          actor.casts.forEach((cast) => {
            const phaseIndex = resolvePhaseIndexAtTime(cast.at)
            cast.at = Math.floor(cast.at + (deltas[phaseIndex] ?? 0))
          })
        })
        next.general?.casts.forEach((cast) => {
          const phaseIndex = resolvePhaseIndexAtTime(cast.at)
          cast.at = Math.floor(cast.at + (deltas[phaseIndex] ?? 0))
        })
        Object.values(next.encounter_assignments?.abilities ?? {}).forEach(
          (ability) => {
            Object.values(ability.cast_overrides ?? {}).forEach((override) => {
              if (typeof override.time !== 'number') return
              const phaseIndex =
                typeof override.phase_index === 'number'
                  ? override.phase_index
                  : resolvePhaseIndexAtTime(override.time)
              override.time = Math.floor(
                override.time + (deltas[phaseIndex] ?? 0),
              )
            })
          },
        )
        next.phase_order = newOrder
      }, true)
    },
    [
      canEdit,
      effectivePhaseDurations,
      phaseDividers,
      phaseOrder,
      phaseStarts,
      resolvePhaseIndexAtTime,
      updateTimeline,
    ],
  )

  const handleTimelineHoverMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (boundaryDragActiveRef.current) return
      const rect = event.currentTarget.getBoundingClientRect()
      const localX = event.clientX - rect.left
      if (rect.width <= 0) {
        setHoverTime(null)
        return
      }
      const ratio = clamp(localX / rect.width, 0, 1)
      const nextTime =
        visibleRange.start + ratio * (visibleRange.end - visibleRange.start)
      setHoverTime(Math.floor(clamp(nextTime, 0, maxTime)))
    },
    [maxTime, visibleRange.end, visibleRange.start],
  )
  const handleTimelineHoverMoveFromCoordinates = useCallback(
    (clientX: number, timelineLeft: number, timelineWidth: number) => {
      if (boundaryDragActiveRef.current) return
      if (timelineWidth <= 0) {
        setHoverTime(null)
        return
      }
      const ratio = clamp((clientX - timelineLeft) / timelineWidth, 0, 1)
      const nextTime =
        visibleRange.start + ratio * (visibleRange.end - visibleRange.start)
      setHoverTime(Math.floor(clamp(nextTime, 0, maxTime)))
    },
    [maxTime, visibleRange.end, visibleRange.start],
  )

  const handleTimelineHoverLeave = useCallback(() => {
    if (boundaryDragActiveRef.current) return
    setHoverTime(null)
  }, [])
  const handleTimelineHoverTimeChange = useCallback(
    (nextHoverTime: number) => {
      setHoverTime(
        Math.floor(clamp(nextHoverTime, visibleRange.start, visibleRange.end)),
      )
    },
    [visibleRange.end, visibleRange.start],
  )
  const stickyTop = useStickyTop()
  const viewerIndexByKey = useMemo(
    () =>
      viewers.reduce<Record<string, number>>((acc, viewer, index) => {
        acc[viewer.key] = index
        return acc
      }, {}),
    [viewers],
  )
  const visibleCursors = useMemo(
    () =>
      cursorContext.startsWith('tab:')
        ? cursors.filter((cursor) => cursor.context === cursorContext)
        : [],
    [cursorContext, cursors],
  )
  const tabCastCounts = useMemo<Record<AbilityType, number>>(() => {
    const counts = abilityTypes.reduce<Record<AbilityType, number>>(
      (acc, type) => {
        acc[type] = 0
        return acc
      },
      {} as Record<AbilityType, number>,
    )
    const abilities = getRaidPlanAbilities()
    const classSpells = abilities.classSpells
    const specSpells = abilities.specSpells
    const generalAbilityIds = new Set(
      generalAbilities.map((ability) => ability.id),
    )
    timeline.actors.forEach((actor) => {
      const classSpellsById = (
        classSpells[actor.class_id.toString()] ?? []
      ).reduce<Record<number, AbilityType>>((acc, spell) => {
        acc[spell.id] = spell.type
        return acc
      }, {})
      const specSpellsById = actor.spec_id
        ? (specSpells[actor.spec_id.toString()] ?? []).reduce<
            Record<number, AbilityType>
          >((acc, spell) => {
            acc[spell.id] = spell.type
            return acc
          }, {})
        : {}
      actor.casts.forEach((cast) => {
        if (cast.hidden) return
        if (cast.spell_id === null) {
          counts.general += 1
          return
        }
        if (generalAbilityIds.has(cast.spell_id)) {
          counts.general += 1
          return
        }
        const castType =
          specSpellsById[cast.spell_id] ?? classSpellsById[cast.spell_id]
        if (!castType) return
        counts[castType] += 1
      })
    })
    const globalGeneralCastCount = (timeline.general?.casts ?? []).filter(
      (cast) => !cast.hidden,
    ).length
    counts.key_cooldown += globalGeneralCastCount
    counts.assignments = getEncounterAssignmentAbilities(timeline).length
    return counts
  }, [
    generalAbilities,
    timeline.actors,
    timeline.encounter_assignments,
    timeline.general?.casts.length,
  ])
  const hideAssignmentsTab = inFocusedDialog
  const hideAddActorBar = inFocusedDialog
  const hideGeneralSection = inFocusedDialog
  const showTimelineOnboarding =
    canEdit &&
    !inFocusedDialog &&
    !isInitializing &&
    selectedType !== 'assignments' &&
    timeline.actors.length === 0
  const showDefaultAddActorBar =
    canEdit &&
    !hideAddActorBar &&
    !showTimelineOnboarding &&
    selectedType !== 'assignments'
  const visibleAbilityTypes = useMemo(
    () =>
      abilityTypes.filter(
        (type) => !(hideAssignmentsTab && type === 'assignments'),
      ),
    [hideAssignmentsTab],
  )
  const showAssignmentsSection =
    !hideAssignmentsTab && selectedType === 'assignments'
  return (
    <div
      className='relative'
      ref={timelineRootRef}
      onMouseMove={handleTimelineMouseMove}
      onMouseLeave={handleTimelineMouseLeave}
    >
      <div className='w-full relative'>
        <div
          className={
            inFocusedDialog
              ? ''
              : cn(
                  'z-30 bg-card pb-1',
                  isEncounterTimelinePinned && 'sticky',
                  isEncounterTimelinePinned && stickyTop,
                )
          }
        >
          <TimelineAbilitySection
            title={
              <div className='flex items-center gap-1.5 min-w-0'>
                {inFocusedDialog ? null : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='h-4 w-4 shrink-0'
                        aria-pressed={isEncounterTimelinePinned}
                        onClick={toggleEncounterTimelinePinned}
                      >
                        <Pin
                          className={cn(
                            'h-4 w-4',
                            isEncounterTimelinePinned
                              ? 'fill-current text-foreground'
                              : 'text-muted-foreground',
                          )}
                        />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {isEncounterTimelinePinned
                        ? t('dashboard.team.plans.unpin-encounter-timeline')
                        : t('dashboard.team.plans.pin-encounter-timeline')}
                    </TooltipContent>
                  </Tooltip>
                )}
                {encounter?.thumbnailUrl ? (
                  <img
                    className='w-4 h-4 rounded-md shrink-0'
                    src={encounter.thumbnailUrl}
                  />
                ) : (
                  <div className='w-4 h-4 rounded-md border bg-muted' />
                )}
                <span className='truncate'>{encounter?.name}</span>
              </div>
            }
            labels={encounterAbilityRows.map((row) => (
              <EncounterAbilityLabel
                key={`${row.spellId}-${row.name}`}
                spellId={row.spellId}
                name={row.name}
                color={row.color}
                categories={row.categories}
              />
            ))}
            timeline={
              <TimelineContainer
                maxTime={maxTime}
                phaseDividers={hasEncounterTimeline ? phaseDividers : []}
                phaseBoundaryHandles={
                  hasEncounterTimeline ? phaseBoundaryHandles : []
                }
                rows={hasEncounterTimeline ? encounterAbilityRows : []}
                hoverTime={visibleHoverTime}
                onPhaseBoundaryDragStart={
                  hasEncounterTimeline && canEdit
                    ? handlePhaseBoundaryDragStart
                    : undefined
                }
                onPhaseBoundaryDrag={
                  hasEncounterTimeline && canEdit
                    ? handlePhaseBoundaryDrag
                    : undefined
                }
                onPhaseBoundaryDragEnd={
                  hasEncounterTimeline && canEdit
                    ? handlePhaseBoundaryDragEnd
                    : undefined
                }
                onSwapPhase={
                  hasEncounterTimeline && canEdit ? swapPhases : undefined
                }
                onResetPhaseDuration={
                  hasEncounterTimeline && canEdit
                    ? handleResetPhaseDuration
                    : undefined
                }
                emptyMessage={
                  hasEncounterTimeline
                    ? undefined
                    : 'Timeline information will be available for this encounter soon'
                }
                className={inFocusedDialog ? 'text-card' : 'text-card-nested'}
              />
            }
            onTimelineMouseMove={handleTimelineHoverMove}
            onTimelineMouseLeave={handleTimelineHoverLeave}
          />

          <Separator className='mt-1' />
        </div>

        <div className='py-2 flex items-center justify-between gap-3 flex-wrap'>
          <Tabs
            value={selectedType}
            onValueChange={(value) => setSelectedType(value as AbilityType)}
          >
            <TabsList className='flex-wrap'>
              {visibleAbilityTypes.map((type) => (
                <TabsTrigger
                  key={type}
                  value={type}
                  className='h-auto py-1 group'
                >
                  <span className='flex flex-col leading-tight text-center gap-0.5'>
                    <span>
                      {t(`dashboard.team.plans.ability-types.${type}`)}
                    </span>
                    <span className='text-[11px] text-muted-foreground/70 font-normal group-data-[state=active]:text-primary-foreground'>
                      {tabCastCounts[type] ?? 0}{' '}
                      {type === 'assignments'
                        ? (tabCastCounts[type] ?? 0) === 1
                          ? 'ability'
                          : 'abilities'
                        : (tabCastCounts[type] ?? 0) === 1
                        ? 'cast'
                        : 'casts'}
                    </span>
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {showDefaultAddActorBar ? <AddActorBar /> : null}
        </div>

        {isInitializing && (
          <div className='py-10 flex items-center justify-center'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        )}

        {showTimelineOnboarding && (
          <div className='p-4 mb-4 flex flex-col items-center gap-2'>
            <div className='flex flex-col gap-0 items-center'>
              <h4 className='mb-0'>Get started with cooldowns</h4>
              <p className='text-muted-foreground text-xs mb-6'>
                or plan{' '}
                <button
                  type='button'
                  className='text-primary hover:text-primary/90'
                  onClick={() => setSelectedType('assignments')}
                >
                  assignments
                </button>{' '}
                for this encounter
              </p>
            </div>
            <div className='flex items-center gap-2 flex-wrap'>
              <Button
                variant='outline'
                className='h-32 w-52 flex flex-col gap-3 items-center justify-center'
                onClick={() => setIsImportDialogOpen(true)}
              >
                <img src={imageUrl('wcl-logo.png')} className='w-8 h-8' />
                {t('dashboard.team.plans.onboarding.import-from-warcraft-logs')}
              </Button>
              <Button
                variant='outline'
                className='h-32 w-52 flex flex-col gap-3 items-center justify-center'
                onClick={() => setIsImportFromNoteDialogOpen(true)}
              >
                <FileTextIcon className='w-8 h-8' />
                {t('dashboard.team.plans.onboarding.import-from-note')}
              </Button>
              <Button
                variant='outline'
                className='h-32 w-52 flex flex-col gap-3 items-center justify-center'
                onClick={() => setIsCopyFromPlanDialogOpen(true)}
              >
                <CopyIcon className='w-8 h-8' />
                {t('dashboard.team.plans.onboarding.copy-from-another-plan')}
              </Button>
            </div>
            <div className='flex items-center gap-3 my-3'>
              <div className='h-px bg-border grow' />
              <span className=''>
                {t('dashboard.team.plans.onboarding.or-add-characters')}
              </span>
              <div className='h-px bg-border grow' />
            </div>
            <Card>
              <AddActorBar />
            </Card>
          </div>
        )}

        {!showTimelineOnboarding &&
          !isInitializing &&
          selectedType !== 'assignments' &&
          !timeline.actors.length &&
          canEdit && (
            <div className='py-10 text-center text-sm text-muted-foreground'>
              {t('dashboard.team.plans.empty-actors')}
            </div>
          )}

        {showAssignmentsSection ? (
          <TeamPlanAssignments />
        ) : (
          <TeamPlanActorRows
            hoverTime={visibleHoverTime}
            onTimelineMouseMove={handleTimelineHoverMove}
            onTimelineMouseLeave={handleTimelineHoverLeave}
            onTimelineHoverTimeChange={handleTimelineHoverTimeChange}
            onActorRowHoverMove={handleTimelineHoverMoveFromCoordinates}
            inFocusedDialog={inFocusedDialog}
            actorRowBehaviorByUuid={actorRowBehaviorByUuid}
            beforeActorContentByUuid={beforeActorContentByUuid}
            afterAbilityActorsContent={
              !hideGeneralSection &&
              !showTimelineOnboarding &&
              selectedType === 'key_cooldown' ? (
                <div className='relative z-10 group'>
                  <ActorHeader title={<span>Everyone</span>} />
                  <div className='flex items-start gap-1'>
                    <ActorAbilityContainer
                      labels={
                        <>
                          {sortedGeneralRows.map((row) => {
                            if (row.spellId === null) {
                              const noteName =
                                generalAbilities.find(
                                  (ability) => ability.id === NOTE_ABILITY_ID,
                                )?.name ?? 'Note'
                              return (
                                <div
                                  key='general-note'
                                  className='flex items-center gap-2'
                                >
                                  <div className='w-40 max-w-40 shrink-0 h-5 text-xs text-foreground truncate flex items-center gap-2'>
                                    <span className='truncate grow text-right'>
                                      {noteName}
                                    </span>
                                    <BlizzardIcon
                                      iconName={NOTE_ICON_NAME}
                                      size='small'
                                      className='w-5 h-5 rounded border border-border'
                                    />
                                  </div>
                                </div>
                              )
                            }
                            const abilityName =
                              generalAbilityById[row.spellId]?.name ??
                              `Spell ${row.spellId}`
                            return (
                              <div
                                key={row.spellId}
                                className='flex items-center gap-2'
                              >
                                <a
                                  href={`https://wowhead.com/spell=${row.spellId}`}
                                  target='_blank'
                                  rel='noreferrer'
                                  className='w-40 max-w-40 shrink-0 h-5 text-xs text-foreground truncate flex items-center gap-2'
                                >
                                  <span className='truncate grow text-right'>
                                    {abilityName}
                                  </span>
                                  <SpellIcon
                                    spellId={row.spellId}
                                    className='w-5 h-5 rounded border border-border'
                                  />
                                </a>
                              </div>
                            )
                          })}
                        </>
                      }
                    />
                    <div
                      className='grow min-w-0'
                      onMouseMove={handleTimelineHoverMove}
                      onMouseLeave={handleTimelineHoverLeave}
                    >
                      <ActorTimelineContainer
                        storageTarget={{ type: 'general' }}
                        rows={sortedGeneralRows}
                        hoverTime={visibleHoverTime}
                        onHoverTimeChange={handleTimelineHoverTimeChange}
                        className={
                          inFocusedDialog ? 'text-card' : 'text-card-nested'
                        }
                      />
                    </div>
                  </div>
                </div>
              ) : null
            }
          />
        )}
        {extraActorContent}

        {visibleCursors.map((cursor) => (
          <div
            key={cursor.viewer.key}
            className='absolute z-40 pointer-events-none'
            style={{
              left: `${cursor.x * 100}%`,
              top: `${cursor.y * 100}%`,
              transition: 'left 120ms linear, top 120ms linear',
              willChange: 'transform',
            }}
          >
            <div
              className={`w-2 h-2 rounded-full ${getViewerColorClass(
                viewerIndexByKey[cursor.viewer.key] ?? 0,
              )}`}
            />
            <div className='text-[10px] px-1 rounded bg-card border border-border mt-0.5 whitespace-nowrap'>
              {getViewerLabel(cursor.viewer.label)}
            </div>
          </div>
        ))}
      </div>
      <ImportFromWarcraftLogsDialog
        open={isImportDialogOpen}
        onOpenChange={setIsImportDialogOpen}
        encounterId={raidPlan.encounterId}
        owner={owner}
        difficulty={raidPlan.difficulty}
        timeline={timeline}
        onImported={(importedTimeline) => {
          applyPlanFromSource({ timeline: importedTimeline })
        }}
      />
      <CopyFromPlanDialog
        open={isCopyFromPlanDialogOpen}
        onOpenChange={setIsCopyFromPlanDialogOpen}
        owner={owner}
        planListPathPrefix={planListPathPrefix}
        currentPlan={raidPlan}
        timeline={timeline}
        onApply={applyPlanFromSource}
        onImportFromExportString={() => setIsImportStringDialogOpen(true)}
      />
      <ImportFromNoteDialog
        open={isImportFromNoteDialogOpen}
        onOpenChange={setIsImportFromNoteDialogOpen}
        raidPlan={raidPlan}
        encounterWclId={encounter?.wclId}
        timeline={timeline}
        rosterMemberships={rosterMemberships}
        onImported={(importedTimeline) => {
          applyPlanFromSource({ timeline: importedTimeline })
        }}
      />
      <PlanImportStringDialog<RaidPlanImportSuccess>
        open={isImportStringDialogOpen}
        onOpenChange={setIsImportStringDialogOpen}
        hasExistingContent={countRaidPlanContent(timeline) > 0}
        description={t('dashboard.team.plans.import-description')}
        invalidMessage={t('dashboard.team.plans.import-invalid')}
        overwriteConfirmMessage={t(
          'dashboard.team.plans.import-overwrite-confirm',
        )}
        parse={(input) => {
          const result = parseRaidPlanImport(input)
          if (!result.ok) return { ok: false }
          return {
            ok: true,
            value: result,
            summary: t('dashboard.team.plans.import-summary', {
              actorCount: result.actorCount,
              castCount: result.castCount,
              assignmentCount: result.assignmentCount,
              visualCount: result.visualCount,
            }),
          }
        }}
        onImport={async (imported) => {
          const remappedTimeline = await resolveImportedRaidPlanTimeline({
            owner,
            destOwner: {
              ownerType: raidPlan.ownerType,
              ownerId: raidPlan.ownerId,
            },
            encounterId: raidPlan.encounterId,
            difficulty: raidPlan.difficulty,
            timeline: imported.timeline,
            visualPlans: imported.visualPlans,
          })
          await queryClient.invalidateQueries({ queryKey: ['visualPlans'] })
          await queryClient.invalidateQueries({ queryKey: ['visualPlan'] })
          applyPlanFromSource({
            timeline: remappedTimeline,
            message: imported.message ?? '',
            includeMessageInExport: imported.includeMessageInExport ?? false,
          })
        }}
      />
    </div>
  )
}

export default TeamPlanTimeline
